import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, CenteredScreen, ErrorText, Field } from '../components/ui';

interface Enrollment {
  factorId: string;
  qrCode?: string;
  secret?: string;
}

/**
 * Staff must verify a TOTP code. First visit: enrol an authenticator app (QR code);
 * later visits: just enter the current code. The database refuses staff data until this succeeds.
 *
 * Reading the factors is a query; enrolling is a one-time mutation. Enrolling inside a query
 * raced with refetches after auth events: the server replaced the factor while the screen still
 * showed the old QR code, and the first code was always rejected.
 */
export function MfaPage() {
  const { t } = useTranslation();
  const { supabase, session, refresh, signOut } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  // not under the ['auth'] key: auth events must not re-run it
  const factors = useQuery({
    queryKey: ['mfa-factors', session?.user.id],
    staleTime: Infinity,
    queryFn: async (): Promise<string | null> => {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;
      return data.totp[0]?.id ?? null;
    },
  });

  const enroll = useMutation({
    mutationFn: async (): Promise<Enrollment> => {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;
      // drop half-finished enrolments from earlier visits, then start a new one
      for (const factor of data.all.filter((f) => f.status === 'unverified')) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }
      const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Parta ${new Date().toISOString()}`,
      });
      if (enrollError) throw enrollError;
      return { factorId: enrolled.id, qrCode: enrolled.totp.qr_code, secret: enrolled.totp.secret };
    },
  });

  const started = useRef(false);
  const { mutate: startEnrollment } = enroll;
  useEffect(() => {
    if (factors.data === null && !started.current) {
      started.current = true;
      startEnrollment();
    }
  }, [factors.data, startEnrollment]);

  const enrollment: { data?: Enrollment; isPending: boolean; error: unknown } = {
    data: factors.data ? { factorId: factors.data } : enroll.data,
    isPending: factors.isPending || (factors.data === null && !enroll.data && !enroll.error),
    error: factors.error ?? enroll.error,
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!enrollment.data) return;
    setBusy(true);
    setError(undefined);
    const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enrollment.data.factorId,
      code: code.trim(),
    });
    setBusy(false);
    if (verifyError) {
      setError(t('auth.mfaInvalidCode'));
      return;
    }
    await refresh();
  }

  const enrolling = Boolean(enrollment.data?.qrCode);

  return (
    <CenteredScreen>
      <Card>
        <h1 className="mb-1 font-display text-[32px] leading-[38px] font-light tracking-[-0.04em] text-ink">
          {enrolling ? t('auth.mfaEnrollTitle') : t('auth.mfaTitle')}
        </h1>
        <p className="mb-6 text-[15px] text-muted">{t('auth.mfaRequired')}</p>
        {enrollment.isPending && <p>{t('common.loading')}</p>}
        <ErrorText>{enrollment.error ? t('common.error') : undefined}</ErrorText>
        {enrollment.data && (
          <form className="space-y-4" onSubmit={onSubmit}>
            {enrolling && (
              <div className="space-y-3">
                <p className="text-sm">{t('auth.mfaEnrollHint')}</p>
                {/* data: URL with an SVG — rendered as an image, so it cannot run scripts */}
                <img
                  src={enrollment.data.qrCode}
                  alt="QR"
                  className="mx-auto h-48 w-48 rounded-[16px] bg-white p-2 ring-1 ring-[#E4E0D7]"
                />
                <p className="break-all text-xs text-text-muted">
                  {t('auth.mfaSecretHint', { secret: enrollment.data.secret ?? '' })}
                </p>
              </div>
            )}
            <Field
              id="code"
              label={t('auth.mfaCode')}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <ErrorText>{error}</ErrorText>
            <Button type="submit" disabled={busy || code.length !== 6}>
              {t('auth.mfaVerify')}
            </Button>
          </form>
        )}
        <button
          type="button"
          className="mt-4 text-sm text-muted underline decoration-[#E4E0D7] underline-offset-4"
          onClick={() => void signOut()}
        >
          {t('common.signOut')}
        </button>
      </Card>
    </CenteredScreen>
  );
}
